import React, { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import _cloneDeep from 'lodash/cloneDeep';

import { useFeatures } from '@atc/features';

import { LinkIcon } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import offsetCalc from '@/utilities/offSetScrollCalculation';
import scrollToElement from '@/utilities/scrollToElement';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import inspectionReportTokenFetcher from '@/fetchers/InspectionReportTokenFetcher';
import VehicleConditioningFetcher from '@/fetchers/vdp/VehicleConditioningFetcher';

import BatteryDegradationJumpLink from '@/components/vehicledetails/BatteryDegradationJumpLink';
import BatteryInfoModal from '@/components/vehicledetails/BatteryInfoModal';
import WarrantyJumpLink from '@/components/vehicledetails/WarrantyJumpLink/WarrantyJumpLink';

let gasMileageDisclaimer = 'Miles per gallon (MPG) shown is estimated for city/highway driving and may be combined. Actual mileage will vary.';
const evRangeDisclaimer = 'EV mile range is the distance an electric vehicle can drive on electricity before the battery depletes.';
const lowMileageDisclaimer = 'This vehicle has a lower mileage than 50% of similar vehicles';
const wellEquippedDisclaimer = 'This vehicle is better equipped than 50% of similar vehicles';

const icons = {
    mileage: 'mileage',
    lowMileage: 'mileage',
    differentiators: 'diamond',
    exterior: 'exterior',
    interiorSeats: 'interiorSeats',
    engineDescription: 'engineDescription',
    transmission: 'transmission',
    driveType: 'driveType',
    mpg: 'mpg',
    truckBedLength: 'truckBedLength',
    evBatteryChargeTime: 'battery',
    windowSticker: 'windowSticker',
    inspectionReport: 'inspectionChecklist',
    warranty: 'warranty',
    wellEquipped: 'diamond',
    batteryDegradationInfo: 'hybridSuvs',
};

// the purpose of specificationKeys is to sort the order of specifications
// NOTE: vehicle reconditioning will always be at the bottom
const componentKeys = {
    grid: [
        'wellEquipped',
        'mileage',
        'engineDescription',
        'mpg', // only apply to non-electric cars
        'batteryDegradationInfo',
        'evBatteryChargeTime', // only apply to hybrid and electric cars
        'exterior',
        'interiorSeats',
        'transmission',
        'driveType',
        'truckBedLength', // only apply to trucks
        'windowSticker',
        'warranty',
        'inspectionReport', // only for FBA vehicles
        'vehicleReconditioning',
    ],
    card: [
        'engineDescription',
        'mpg',
        'transmission',
        'driveType',
        'truckBedLength',
    ],
    subbar: [
        'mileage',
        'differentiators', // { Grouped differentiators: lowMileage, wellEquipped, reconditioning } if applicable
        'windowSticker', // Window Sticker
        'inspectionReport', // Inspection Checklist (can be included for Ford as requested)
        'exterior', // { Exterior Color }
        'interiorSeats', // { Interior Color }
    ],
};

const createGroupedDifferentiators = (specifications, vehicleReconditioningSpecs) => {
    const differentiatorItems = [];

    // Add lowMileage if it exists
    if (specifications.lowMileage) {
        differentiatorItems.push('Low Miles');
    }

    // Add wellEquipped if it exists
    if (specifications.wellEquipped) {
        differentiatorItems.push('Well Equipped');
    }

    // Add vehicle reconditioning items
    const vehicleReconditioningValuesMap = {
        BRAKES: 'New Brakes',
        DETAILING: 'Detailed',
        TIRES: 'New Tires',
    };

    if (vehicleReconditioningSpecs?.services?.length) {
        for (const service of vehicleReconditioningSpecs.services) {
            differentiatorItems.push(vehicleReconditioningValuesMap[service.servicePerformed]);
        }
    }

    if (differentiatorItems.length > 0) {
        return {
            key: 'differentiators',
            specData: {
                value: differentiatorItems,
                label: 'DIFFERENTIATORS',
                icon: 'diamond',
                isDifferentiator: true,
            },
        };
    }

    return null;
};

const addSectionIcon = (specifications, key) => {
    if (specifications?.[key]) {
        specifications[key].icon = icons[key];
    }
};

const handleFuelTypeGroup = (fuelTypeGroup, engineDescription) => {
    switch (fuelTypeGroup) {
        case 'Hybrid: Gas/Electric':
            engineDescription.value = fuelTypeGroup;
            break;
        case 'Plug-in Hybrid: Gas/Electric':
            engineDescription.value = fuelTypeGroup;
            break;
        default:
            break;
    }
};

const setupBatterySpecs = (specifications, enableBatteryHealth, fordBatteryHealth, electricVehicleRange, electricComponentInfo) => {
    if (enableBatteryHealth && fordBatteryHealth) {
        if (electricVehicleRange > 0) {
            specifications.batteryDegradationInfo = {
                label: 'EV RANGE',
                value: `${electricVehicleRange.toLocaleString('en-US')} mi Range When New`,
            };
        }

        if (electricComponentInfo?.epaChargeTimeAt240V) {
            specifications.evBatteryChargeTime = {
                value: `Estimated ${electricComponentInfo.epaChargeTimeAt240V} hr Charging Time`,
            };
        }
    }
};

const setupWarrantySpec = (specifications, warrantyProducts) => {
    if (warrantyProducts) {
        const handleWarrantyLinkClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            scrollToElement('purchaseConfidence', offsetCalc(25, 'sticky-header-vdp'));
        };
        specifications.warranty = {
            label: 'Purchase With Confidence',
            value: <WarrantyJumpLink
                {...warrantyProducts}
                onClick={handleWarrantyLinkClick}
            />,
        };
        specifications.warranty.icon = 'warranty';
    }
};

const setupMpgSpec = (specifications, config) => {
    const { fuelTypeGroup, fuelType, mpgCity, mpgHighway, nameplate, makeCode, disclaimers } = config;

    // Some dealerships require specific wording for the MPG disclaimer
    if (nameplate.includes(makeCode) && Object.keys(disclaimers).includes(makeCode)) {
        gasMileageDisclaimer = disclaimers[makeCode];
    }
    if (!specifications.range && fuelTypeGroup !== 'Electric' && fuelType?.name !== 'Electric') {
        specifications.mpg = {
            label: 'MPG',
            value: mpgCity && mpgHighway ? mpgCity + ' City / ' + mpgHighway + ' Highway' : '',
            disclaimer: gasMileageDisclaimer,
        };
    }
};

const createLinkIconSpec = (config) => {
    const {
        specKey,
        label,
        subbarLabel,
        fullLabel,
        analyticsEvent,
        href,
        onClick,
        isSubbar,
    } = config;

    const displayLabel = isSubbar ? subbarLabel : fullLabel;

    const handleClick = (event) => {
        sendClick(analyticsEvent, event, { co_txt_url: displayLabel });
        if (onClick) {
            onClick(event);
        }
    };

    return {
        [specKey]: {
            label,
            value: (
                <LinkIcon
                    undecorated={isSubbar}
                    href={href}
                    glyph={isSubbar ? '' : 'offsite'}
                    target={href ? '_blank' : undefined}
                    rel={href ? 'nofollow noopener' : undefined}
                    onClick={handleClick}
                >
                    {displayLabel}
                </LinkIcon>
            ),
        },
    };
};

const setupWindowStickerSpec = (specifications, windowStickerHref, isSubbar) => {
    if (windowStickerHref) {
        const linkSpec = createLinkIconSpec({
            specKey: 'windowSticker',
            label: 'WINDOW STICKER',
            subbarLabel: 'Window Sticker',
            fullLabel: 'View Original Window Sticker',
            analyticsEvent: 'windowStickerClick',
            href: windowStickerHref,
            isSubbar,
        });
        Object.assign(specifications, linkSpec);
    }
};

const setupInspectionReportSpec = (specifications, isInspectionChecklistEnabled, updateInspectionChecklistUrl, isSubbar) => {
    if (isInspectionChecklistEnabled) {
        const handleInspectionReportClick = (event) => {
            // This window.open hack is needed because iOS wont allow us to call window.open inside of an async
            // function. So we call the new tab outside of the async function then set the url location once we
            // get the authorization token back from the fetcher.
            const newTab = window.open('about:blank');
            updateInspectionChecklistUrl().then((url) => {
                newTab.location = url;
            });
        };

        const linkSpec = createLinkIconSpec({
            specKey: 'inspectionReport',
            label: 'INSPECTION REPORT',
            subbarLabel: 'Inspection Checklist',
            fullLabel: 'View Inspection Checklist',
            analyticsEvent: 'inspectionChecklistClick',
            onClick: handleInspectionReportClick,
            isSubbar,
        });
        Object.assign(specifications, linkSpec);
    }
};

const setupVehicleQualitySpecs = (specifications, vinSightData, enableWellEquippedVehicle, enableLowMileage, listingType) => {
    if (vinSightData?.equipmentIndex >= 5 && enableWellEquippedVehicle) {
        specifications.wellEquipped = {
            label: 'WELL-EQUIPPED VEHICLE',
            value: 'Well-Equipped Vehicle',
            disclaimer: wellEquippedDisclaimer,
        };
    }

    if (listingType !== 'New' && vinSightData?.vehicleMileageIndex <= 5 && enableLowMileage) {
        // Create a separate lowMileage specification for differentiators
        specifications.lowMileage = {
            label: 'LOW MILEAGE',
            value: 'Low Mileage',
            icon: 'mileage',
            disclaimer: lowMileageDisclaimer,
        };

        // Still update the regular mileage spec as before
        specifications.mileage = {
            label: 'LOW MILEAGE',
            value: specifications?.mileage?.value,
            icon: 'mileage',
            disclaimer: lowMileageDisclaimer,
        };
    }
};

const enhanceMileageValue = (mileage) => {
    if (mileage.value && !mileage.value.toLowerCase().includes('mi')) {
        const mileageNumber = mileage.value.replace(/[^0-9,]/g, '');
        if (mileageNumber) {
            mileage.value = `${mileageNumber} mi`;
        }
    }
};

const appendValueSuffix = (spec, checkStrings, suffix) => {
    if (!spec.value) return;

    const hasAnyString = Array.isArray(checkStrings)
        ? checkStrings.some((str) => spec.value.toLowerCase().includes(str.toLowerCase()))
        : spec.value.toLowerCase().includes(checkStrings.toLowerCase());

    if (!hasAnyString) {
        spec.value += suffix;
    }
};

const enhanceColorValues = (exterior, interiorSeats, exteriorColorSimple, interiorColorSimple) => {
    // exterior
    appendValueSuffix(exterior, 'Exterior', ' Exterior');
    exterior.colorValue = exteriorColorSimple;

    // interiorSeat
    appendValueSuffix(interiorSeats, ['seat', 'Interior'], ' Interior');
    interiorSeats.colorValue = interiorColorSimple;
};

const enhanceTransmissionValue = (transmission) => {
    appendValueSuffix(transmission, 'Transmission', ' Transmission');
};

const enhanceTruckBedValue = (truckBedLength, truckBedSizeDescription) => {
    if (Object.keys(truckBedLength).length && Object.keys(truckBedSizeDescription).length) {
        if (truckBedLength.value && !truckBedLength.value.includes('Bed') && truckBedSizeDescription.value) {
            truckBedLength.value += ` Bed Length (${truckBedSizeDescription.value})`;
        }
    }
};

const enhanceBatteryValues = (evBatteryChargeTime, batteryDegradationInfo, displayBatteryHealth) => {
    if (evBatteryChargeTime?.value) {
        evBatteryChargeTime.ctaEl = <BatteryInfoModal />;
    }

    if (displayBatteryHealth && batteryDegradationInfo?.label === 'USABLE RANGE') {
        batteryDegradationInfo.ctaEl = <BatteryDegradationJumpLink ctaCopy="View Details" />;
    } else {
        batteryDegradationInfo.disclaimer = evRangeDisclaimer;
    }
};

const enhanceSpecificationValues = (specifications, fuelTypeGroup, displayBatteryHealth, exteriorColorSimple, interiorColorSimple) => {
    const {
        engineDescription,
        exterior = {},
        evBatteryChargeTime = {},
        interiorSeats = {},
        mileage = {},
        transmission = {},
        truckBedLength = {},
        truckBedSizeDescription = {},
        batteryDegradationInfo = {},
    } = specifications;

    // icon attribute will be used a key to render SpecificationIcon in SpecificationList.js
    Object.keys(specifications).forEach((key) => addSectionIcon(specifications, key));

    handleFuelTypeGroup(fuelTypeGroup, engineDescription);
    enhanceBatteryValues(evBatteryChargeTime, batteryDegradationInfo, displayBatteryHealth);
    enhanceMileageValue(mileage);
    enhanceColorValues(exterior, interiorSeats, exteriorColorSimple, interiorColorSimple);
    enhanceTransmissionValue(transmission);
    enhanceTruckBedValue(truckBedLength, truckBedSizeDescription);
};

const useVehicleSpecsData = (type = 'grid') => {

    let specificationItems = [];

    const {
        battery_health: [enableBatteryHealth, {
            ford_battery_health: fordBatteryHealth,
        }],
        specifications: [, {
            low_mileage: enableLowMileage, // enabled for all but Ford and Car&Driver
            well_equipped_vehicle: enableWellEquippedVehicle, // enabled for all but Ford and Car&Driver
        }],
        inspection_checklist: [isInspectionChecklistEnabled],
        vdp_safeguard: [isSafeguardEnabled],
        custom_mpg_disclaimers: [, { disclaimers }],
        disable_vehicle_reconditioning: [disableVehicleReconditioning],
    } = useFeatures([
        'battery_health',
        'specifications',
        'inspection_checklist',
        'vdp_safeguard',
        'custom_mpg_disclaimers',
        'disable_vehicle_reconditioning',
    ]);

    const [vehicleReconditioningSpecifications, setVehicleReconditioningSpecifications] = useState();

    const {
        electricVehicleRange = '',
        esntial = {},
        owner: { nameplate = [] } = {},
        mpgCity,
        mpgHighway,
        specifications: initSpecifications = {},
        fyuseId,
        vin,
        listingType,
        exteriorColorSimple,
        interiorColorSimple,
        windowSticker: { href: windowStickerHref } = {},
        returnPolicy = {},
        moneyBackGuarantee,
        safeGuardProgram,
        fordSalesCode,
        makeCode,
        fuelType,
        fuelTypeGroup = '',
        vinSightData = {},
        electricComponentInfo = {},
    } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const batteryHealthExclusions = electricComponentInfo?.batteryDegradationInfo?.ratingSource !== 'OEM';
    const displayBatteryHealth = (enableBatteryHealth && !fordBatteryHealth) && electricComponentInfo?.batteryDegradationInfo && batteryHealthExclusions;

    const specifications = useMemo(() => _cloneDeep(initSpecifications), [initSpecifications]);

    let warrantyProducts = {
        returnPolicy: Object.values(returnPolicy).filter(Boolean).length ? returnPolicy : undefined,
        moneyBackGuarantee: moneyBackGuarantee || undefined,
        safeGuardProgram,
    };

    warrantyProducts = Object.values(warrantyProducts).filter(Boolean).length ? warrantyProducts : undefined;

    const updateInspectionChecklistUrl = (async () => {
        const response = await inspectionReportTokenFetcher();
        const token = response?.authorization_token;
        const updatedSalesCode = fordSalesCode ? fordSalesCode.replace(/\D/g, '') : '';
        return `https://dfc.concentrix.com/dfc/inspection-checklist?vin=${vin}&authorization_token=${token}&sales_code=${updatedSalesCode}`;
    });

    useEffect(() => {
        if (!disableVehicleReconditioning && vin && fyuseId) {
            (async () => {
                const vehicleReconditioningResponse = await VehicleConditioningFetcher(vin);
                setVehicleReconditioningSpecifications(vehicleReconditioningResponse);
            })();
        }
    }, [disableVehicleReconditioning, fyuseId, vin]);

    // Jump link only enabled if safeguard FF is on (even if other data exist) or listing is Esntial ineligible
    if (!isSafeguardEnabled || Object.values(esntial).filter(Boolean).length) {
        warrantyProducts = null;
    }

    const isSubbar = type === 'subbar';

    setupBatterySpecs(specifications, enableBatteryHealth, fordBatteryHealth, electricVehicleRange, electricComponentInfo);
    setupWarrantySpec(specifications, warrantyProducts);
    setupMpgSpec(specifications, { fuelTypeGroup, fuelType, mpgCity, mpgHighway, nameplate, makeCode, disclaimers });
    setupWindowStickerSpec(specifications, windowStickerHref, isSubbar);
    setupInspectionReportSpec(specifications, isInspectionChecklistEnabled, updateInspectionChecklistUrl, isSubbar);
    setupVehicleQualitySpecs(specifications, vinSightData, enableWellEquippedVehicle, enableLowMileage, listingType);

    if (Object.keys(specifications).length) {
        enhanceSpecificationValues(specifications, fuelTypeGroup, displayBatteryHealth, exteriorColorSimple, interiorColorSimple);
    }

    // Append vehicle reconditioning data to the bottom of specifications
    const specificationKeys = componentKeys[type];

    specificationItems = specificationKeys.reduce((items, key) => {
        if (key === 'differentiators') {
            // Create grouped differentiators for subbar
            const groupedDifferentiators = createGroupedDifferentiators(specifications, vehicleReconditioningSpecifications);
            if (groupedDifferentiators) {
                return items.concat(groupedDifferentiators);
            }
            return items;
        }
        if (key in specifications) {
            return items.concat({ key, specData: specifications[key] });
        }
        return items;
    }, []);

    return specificationItems;
};

export default useVehicleSpecsData;
